import { useInfiniteQuery } from '@tanstack/react-query';

import { QueryError } from '@/components/QueryError';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';

// Réponse de GET /integrations/webhooks/:id/deliveries (tenants-service) : tableau simple, plus récent d'abord
export interface WebhookDelivery {
  id: string; eventType: string; status: 'PENDING' | 'SUCCEEDED' | 'FAILED' | 'RETRYING';
  attempts: number; responseStatus: number | null; errorMsg: string | null;
  lastAttemptAt: string | null; createdAt: string;
}

export const DELIVERIES_PAGE_SIZE = 50;

const STATUS: Record<WebhookDelivery['status'], { label: string; variant: 'success' | 'secondary' | 'destructive' | 'warning' }> = {
  SUCCEEDED: { label: 'Livré', variant: 'success' },
  PENDING: { label: 'En attente', variant: 'secondary' },
  RETRYING: { label: 'Nouvel essai prévu', variant: 'warning' },
  FAILED: { label: 'Échec', variant: 'destructive' },
};

export const deliveriesQueryKey = (endpointId: string) => ['webhook-deliveries', endpointId];

/** Historique des livraisons d'un webhook, par pages de 50 (« Voir plus » tant que la dernière page est pleine). */
export function WebhookDeliveries({ endpointId }: { endpointId: string }) {
  const query = useInfiniteQuery({
    queryKey: deliveriesQueryKey(endpointId),
    initialPageParam: 0,
    queryFn: async ({ pageParam }) =>
      (await api.get<WebhookDelivery[]>(`/integrations/webhooks/${endpointId}/deliveries`, {
        params: { limit: DELIVERIES_PAGE_SIZE, offset: pageParam },
      })).data,
    getNextPageParam: (last, pages) =>
      last.length === DELIVERIES_PAGE_SIZE ? pages.length * DELIVERIES_PAGE_SIZE : undefined,
  });
  const deliveries = query.data?.pages.flat();

  return (
    <div className="mt-3 space-y-1 border-t pt-3">
      {deliveries?.map((d) => (
        <div key={d.id} className="flex items-center justify-between gap-3 text-xs">
          <span className="font-mono">{d.eventType}</span>
          <span className="text-muted-foreground">{new Date(d.lastAttemptAt ?? d.createdAt).toLocaleString('fr-FR')}</span>
          <span className="text-muted-foreground">
            {d.responseStatus ? `HTTP ${d.responseStatus}` : d.errorMsg ?? '—'} · {d.attempts} essai{d.attempts > 1 ? 's' : ''}
          </span>
          <Badge variant={STATUS[d.status].variant}>{STATUS[d.status].label}</Badge>
        </div>
      ))}
      <QueryError query={query} />
      {deliveries?.length === 0 && <p className="text-xs text-muted-foreground">Aucune livraison</p>}
      {query.hasNextPage && (
        <Button size="sm" variant="outline" disabled={query.isFetchingNextPage} onClick={() => void query.fetchNextPage()}>
          {query.isFetchingNextPage ? 'Chargement…' : 'Voir plus'}
        </Button>
      )}
    </div>
  );
}
